// StaffLeaveModal.jsx - Manage staff leaves for the next 30 days.
// Web port of UlloorStyles@e7a5f9b:src/components/StaffLeaveModal.js. A fixed 30-day date grid
// (buttons, not <input type="date">) keeps the reachable date range identical to mobile's, so a
// web edit can never create a leave record for a date mobile's own grid can't even navigate to.
// Aggregate leave counts when no named roster; per-staff-id checklist once staff are named
// (split into Men's/Women's sections for unisex salons).
import React, { useEffect, useRef, useState } from 'react';
import '../pages/salon/SalonStaffManagement.css';

const DAYS_AHEAD = 30;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const generateDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < DAYS_AHEAD; i += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
};

function StaffLeaveModal({
  isOpen,
  onClose,
  onSave,
  salonData,
  existingLeaves = {},
  isUnisex = false,
  isSaving = false,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [leaveCount, setLeaveCount] = useState(0);
  const [menLeaveCount, setMenLeaveCount] = useState(0);
  const [womenLeaveCount, setWomenLeaveCount] = useState(0);
  const [selectedStaffIds, setSelectedStaffIds] = useState(() => new Set());
  const [leaves, setLeaves] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const dayEditorRef = useRef(null);

  const menRoster = salonData?.menStaffList || [];
  const womenRoster = salonData?.womenStaffList || [];
  const staffRoster = salonData?.staffList || [];
  const hasRoster = isUnisex
    ? (menRoster.length > 0 || womenRoster.length > 0)
    : staffRoster.length > 0;

  useEffect(() => {
    if (isOpen) {
      setLeaves(existingLeaves || {});
      setSelectedDate(null);
      setHasChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Scroll the staff/day editor into view as soon as a date is picked, so the user doesn't have
  // to manually scroll past the date grid to reach the staff list on every selection.
  useEffect(() => {
    if (selectedDate && dayEditorRef.current) {
      dayEditorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedDate]);

  if (!isOpen) return null;

  const handleDateSelect = (date) => {
    const dateStr = formatDate(date);
    setSelectedDate(dateStr);

    const existingLeave = leaves[dateStr];
    if (existingLeave) {
      setLeaveCount(existingLeave.leaveCount ?? 0);
      setMenLeaveCount(existingLeave.menLeaveCount ?? 0);
      setWomenLeaveCount(existingLeave.womenLeaveCount ?? 0);
      setSelectedStaffIds(new Set(existingLeave.staffIds || []));
    } else {
      setLeaveCount(0);
      setMenLeaveCount(0);
      setWomenLeaveCount(0);
      setSelectedStaffIds(new Set());
    }
  };

  const toggleStaffOnLeave = (staffId) => {
    setSelectedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };

  const handleSetLeave = () => {
    if (!selectedDate) return;

    let leaveData;
    if (hasRoster) {
      const staffIds = [...selectedStaffIds];
      const menOnLeave = isUnisex ? staffIds.filter((id) => menRoster.some((s) => s.id === id)).length : 0;
      const womenOnLeave = isUnisex ? staffIds.filter((id) => womenRoster.some((s) => s.id === id)).length : 0;
      leaveData = {
        date: selectedDate,
        staffIds,
        menLeaveCount: menOnLeave,
        womenLeaveCount: womenOnLeave,
        leaveCount: isUnisex ? Math.max(menOnLeave, womenOnLeave) : staffIds.length,
      };
    } else {
      leaveData = isUnisex
        ? { date: selectedDate, menLeaveCount, womenLeaveCount, leaveCount: Math.max(menLeaveCount, womenLeaveCount) }
        : { date: selectedDate, leaveCount, menLeaveCount: 0, womenLeaveCount: 0 };
    }

    const isEmpty = hasRoster
      ? selectedStaffIds.size === 0
      : (leaveData.leaveCount === 0 && leaveData.menLeaveCount === 0 && leaveData.womenLeaveCount === 0);

    if (isEmpty) {
      const newLeaves = { ...leaves };
      delete newLeaves[selectedDate];
      setLeaves(newLeaves);
    } else {
      setLeaves((prev) => ({ ...prev, [selectedDate]: leaveData }));
    }

    setHasChanges(true);
    setSelectedDate(null);
  };

  const handleClearLeave = () => {
    if (!selectedDate) return;
    const newLeaves = { ...leaves };
    delete newLeaves[selectedDate];
    setLeaves(newLeaves);
    setHasChanges(true);
    setSelectedDate(null);
    setLeaveCount(0);
    setMenLeaveCount(0);
    setWomenLeaveCount(0);
    setSelectedStaffIds(new Set());
  };

  const handleSave = () => {
    onSave(leaves);
  };

  const getMaxLeaveCount = (gender = null) => {
    if (isUnisex && gender) {
      return gender === 'men'
        ? (salonData?.genderStaffCount?.men?.count ?? salonData?.staffCount ?? 1)
        : (salonData?.genderStaffCount?.women?.count ?? 1);
    }
    return salonData?.staffCount ?? 1;
  };

  const totalLeaveDays = Object.keys(leaves).length;

  const renderStaffLeaveRow = (staff) => {
    const onLeave = selectedStaffIds.has(staff.id);
    return (
      <button
        type="button"
        key={staff.id}
        className={`staff-day-row ${onLeave ? 'staff-day-row-selected leave-selected' : ''}`}
        onClick={() => toggleStaffOnLeave(staff.id)}
      >
        <span>{staff.name || 'Unnamed staff'}</span>
        <span className="staff-day-row-action">{onLeave ? '✓ On leave' : 'Mark on leave'}</span>
      </button>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content scheduling-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>📅 Manage Staff Leaves</h3>
            <p className="modal-subtitle">Select a date to set staff on leave for that day</p>
            {totalLeaveDays > 0 && (
              <div className="leave-summary-badge">
                {totalLeaveDays} day{totalLeaveDays !== 1 ? 's' : ''} with leave scheduled
              </div>
            )}
          </div>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        <div className="modal-body scrollable-body">
          <div className="date-grid">
            {generateDates().map((date) => {
              const dateStr = formatDate(date);
              const leaveData = leaves[dateStr];
              const hasLeave = leaveData && (leaveData.leaveCount > 0 || leaveData.menLeaveCount > 0 || leaveData.womenLeaveCount > 0);
              const isSelected = selectedDate === dateStr;
              const isToday = formatDate(new Date()) === dateStr;
              const isSunday = date.getDay() === 0;

              return (
                <button
                  type="button"
                  key={dateStr}
                  className={[
                    'date-cell',
                    hasLeave && 'date-cell-flagged',
                    isSelected && 'date-cell-selected',
                    isToday && 'date-cell-today',
                    isSunday && !hasLeave && 'date-cell-sunday',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDateSelect(date)}
                >
                  {isToday && <span className="date-cell-today-badge">Today</span>}
                  <span className="date-cell-day">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <span className="date-cell-number">{date.getDate()}</span>
                  <span className="date-cell-month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                  {hasLeave && (
                    <span className="date-cell-flag-badge">
                      {isUnisex ? `👨${leaveData.menLeaveCount} 👩${leaveData.womenLeaveCount}` : `${leaveData.leaveCount} off`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDate && (
            <div className="day-editor" ref={dayEditorRef}>
              <div className="day-editor-title">
                {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>

              {hasRoster ? (
                <>
                  {isUnisex ? (
                    <>
                      {menRoster.length > 0 && (
                        <div className="staff-section">
                          <div className="staff-section-title">👨 Men's Staff</div>
                          {menRoster.map(renderStaffLeaveRow)}
                        </div>
                      )}
                      {womenRoster.length > 0 && (
                        <div className="staff-section">
                          <div className="staff-section-title">👩 Women's Staff</div>
                          {womenRoster.map(renderStaffLeaveRow)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="staff-section">
                      <div className="staff-section-title">Staff</div>
                      {staffRoster.map(renderStaffLeaveRow)}
                    </div>
                  )}
                </>
              ) : isUnisex ? (
                <>
                  <div className="counter-row">
                    <span className="counter-row-label">👨 Men's Staff on Leave</span>
                    <div className="counter-controls">
                      <button type="button" className="counter-btn" onClick={() => setMenLeaveCount(Math.max(0, menLeaveCount - 1))} disabled={menLeaveCount <= 0}>−</button>
                      <div className="counter-display">
                        <span className="counter-value">{menLeaveCount}</span>
                        <span className="counter-unit">/ {getMaxLeaveCount('men')}</span>
                      </div>
                      <button type="button" className="counter-btn" onClick={() => setMenLeaveCount(Math.min(getMaxLeaveCount('men'), menLeaveCount + 1))} disabled={menLeaveCount >= getMaxLeaveCount('men')}>+</button>
                    </div>
                  </div>
                  <div className="counter-row">
                    <span className="counter-row-label">👩 Women's Staff on Leave</span>
                    <div className="counter-controls">
                      <button type="button" className="counter-btn" onClick={() => setWomenLeaveCount(Math.max(0, womenLeaveCount - 1))} disabled={womenLeaveCount <= 0}>−</button>
                      <div className="counter-display">
                        <span className="counter-value">{womenLeaveCount}</span>
                        <span className="counter-unit">/ {getMaxLeaveCount('women')}</span>
                      </div>
                      <button type="button" className="counter-btn" onClick={() => setWomenLeaveCount(Math.min(getMaxLeaveCount('women'), womenLeaveCount + 1))} disabled={womenLeaveCount >= getMaxLeaveCount('women')}>+</button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="counter-row">
                  <span className="counter-row-label">Staff on Leave</span>
                  <div className="counter-controls">
                    <button type="button" className="counter-btn" onClick={() => setLeaveCount(Math.max(0, leaveCount - 1))} disabled={leaveCount <= 0}>−</button>
                    <div className="counter-display">
                      <span className="counter-value">{leaveCount}</span>
                      <span className="counter-unit">/ {getMaxLeaveCount()}</span>
                    </div>
                    <button type="button" className="counter-btn" onClick={() => setLeaveCount(Math.min(getMaxLeaveCount(), leaveCount + 1))} disabled={leaveCount >= getMaxLeaveCount()}>+</button>
                  </div>
                </div>
              )}

              <div className="day-editor-actions">
                {leaves[selectedDate] && (
                  <button type="button" className="clear-btn" onClick={handleClearLeave}>Clear Leave</button>
                )}
                <button type="button" className="set-btn" onClick={handleSetLeave}>
                  {leaves[selectedDate] ? 'Update Leave' : 'Set Leave'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-modal-btn">Cancel</button>
          <button onClick={handleSave} className="save-modal-btn" disabled={isSaving || !hasChanges}>
            {isSaving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StaffLeaveModal;
